import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useAutomationId from '@/hooks/useAutomationId.ts';
import { useEditVersionContext } from '@/context/EditVersionContext/useEditVersionContext.ts';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import {
  AutomationLocalSubflowOutputPort,
  AutomationLocalSubflowPort,
  AutomationVersion,
  BusinessOutcomeAssessmentDeclaration,
} from '@/types/automations.ts';
import { AddableStepType, DataInput } from '@/types/automationFields.ts';
import { LocalSubflowCallStep, SubAutomationTriggerInputMapping } from '@/types/automationSteps.ts';
import {
  get_automation_version_url,
  get_composition_apply_operations_url,
  get_composition_compile_preview_url,
} from '@/utils/urls.ts';
import { notifyStepMutationError } from '@/services/stepMutationErrors.ts';

type EditorCompositionOperationType =
  | 'create_local_subflow'
  | 'rename_local_subflow'
  | 'replace_local_subflow_interface'
  | 'delete_local_subflow'
  | 'create_local_subflow_call'
  | 'delete_local_subflow_call'
  | 'replace_local_subflow_call_bindings'
  | 'replace_subautomation_target'
  | 'replace_subautomation_input_mappings'
  | 'replace_data_input_group'
  | 'replace_integration_selection'
  | 'update_integration_config_fields'
  | 'replace_browser_secret_binding_contract'
  | 'set_business_outcome_assessment'
  | 'set_authorable_field'
  | 'update_local_subflow_step_fields'
  | 'create_local_subflow_step'
  | 'duplicate_local_subflow_step'
  | 'delete_local_subflow_step'
  | 'move_local_subflow_step';

type LocalSubflowCallBindings = Pick<LocalSubflowCallStep, 'input_bindings' | 'output_bindings'>;
export type BrowserSecretBinding = {
  alias: string;
  sensitive_key: string;
  allowed_domains: string[];
};

export type CompositionOperation = {
  op_type: EditorCompositionOperationType;
  step_id?: string;
  step_type?: AddableStepType;
  value?: BrowserSecretBinding[] | string | LocalSubflowCallBindings | SubAutomationTriggerInputMapping[] | {
    target_automation_id: string;
    target_automation_version_id: string;
    trigger_input_mappings: SubAutomationTriggerInputMapping[];
    execution_mode: string;
  };
  fields?: Record<string, unknown>;
  field_name?: string;
  inputs?: DataInput[];
  integration_id?: string | null;
  action_type?: string | null;
  config_fields?: Record<string, unknown>;
  expected_current_value_token?: string | null;
  expected_current_value_sha256?: string | null;
  business_outcome_assessment_action?: 'set' | 'clear';
  business_outcome_assessment?: BusinessOutcomeAssessmentDeclaration | null;
  subflow_id?: string;
  subflow_name?: string;
  subflow_description?: string;
  subflow_input_ports?: AutomationLocalSubflowPort[];
  subflow_output_ports?: AutomationLocalSubflowOutputPort[];
  target_index?: number;
  target_child_group_key?: string | null;
  target_parent_step_id?: string | null;
  expected_current_parent_step_id?: string | null;
  expected_current_parent_path?: string | null;
};

type CompositionPreviewResponse = {
  preview_digest: string | null;
  eligible: boolean;
  blocking_reasons: string[];
  operation_errors: Array<{ message: string }>;
  validation_issues: Array<{ message: string }>;
};

type CompositionApplyResponse = {
  new_version_id: string;
};

export type CompositionValidationFeedback = {
  messages: string[];
};

export type CompositionMutationOptions = {
  onSuccess?: (version: AutomationVersion) => void;
  onError?: () => void;
};

/** Apply an Editor composition edit only through server-compiled operations. */
const useCompositionOperations = () => {
  const apiClient = useApiClient();
  const automationId = useAutomationId();
  const { addToQueue, reportLatestDraftChanged } = useEditVersionContext();
  const queryClient = useQueryClient();
  const [pendingCount, setPendingCount] = useState(0);
  const [validationFeedback, setValidationFeedback] = useState<CompositionValidationFeedback | null>(null);

  const mutate = (operations: CompositionOperation[], options: CompositionMutationOptions = {}) => {
    setPendingCount((count) => count + 1);
    setValidationFeedback(null);
    addToQueue<AutomationVersion, unknown>({
      mutationFn: async (latestVersion) => {
        const preview = await apiClient.post<CompositionPreviewResponse>(
          get_composition_compile_preview_url(automationId),
          {
            base_version_id: latestVersion.id,
            expected_latest_version_id: latestVersion.id,
            operations,
          },
          { suppressGlobalErrorToast: true },
        );
        if (!preview.data.eligible || !preview.data.preview_digest) {
          const messages = [
            ...preview.data.operation_errors.map((error) => error.message),
            ...preview.data.validation_issues.map((issue) => issue.message),
            ...preview.data.blocking_reasons,
          ];
          const uniqueMessages = [...new Set(messages)].filter(Boolean);
          setValidationFeedback({
            messages: uniqueMessages,
          });
          throw new Error(
            uniqueMessages[0] ?? 'The proposed composition edit did not produce an eligible preview.',
          );
        }
        const applied = await apiClient.post<CompositionApplyResponse>(
          get_composition_apply_operations_url(automationId),
          {
            base_version_id: latestVersion.id,
            expected_latest_version_id: latestVersion.id,
            operations,
            expected_preview_digest: preview.data.preview_digest,
          },
          { suppressGlobalErrorToast: true },
        );
        const created = await apiClient.get<AutomationVersion>(get_automation_version_url(applied.data.new_version_id));
        return created.data;
      },
      onSuccess: (version) => {
        void queryClient.invalidateQueries({
          queryKey: ['editor-composition-capabilities', automationId],
        });
        void queryClient.invalidateQueries({
          queryKey: ['editor-local-subflow-context', automationId],
        });
        options.onSuccess?.(version);
      },
      onError: (error) => {
        notifyStepMutationError('apply composition edit', error, reportLatestDraftChanged);
        options.onError?.();
      },
      onComplete: () => setPendingCount((count) => count - 1),
    });
  };

  return { mutate, isPending: pendingCount > 0, validationFeedback };
};

export default useCompositionOperations;
